import React, { createContext, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Heading, type HeadingTagType, type HeadingVariant } from "@purpur/heading";
import c from "classnames/bind";

import styles from "./accordion.module.scss";
import { AccordionItem } from "./accordion-item";

const cx = c.bind(styles);
const rootClassName = "purpur-accordion";

export type AccordionProps = Omit<BaseProps, "title" | "children"> & {
  children: ReactNode;
  negative?: boolean;
  title?: ReactNode;
  titleTag?: HeadingTagType;
  titleVariant?: HeadingVariant;
  /**
   * A schema enables external applications to parse the content in a structured manner.
   * See more at schema.org . We use a JSON-LD based script-tag to add the schema to the DOM.
   * Note that only one type of schema can be added per page
   */
  schema?: string;
};

export const AccordionNegativeContext = createContext(false);

export const Accordion = ({
  children,
  className,
  negative = false,
  title,
  titleTag = "h2",
  titleVariant = "display-15",
  schema,
  ...props
}: AccordionProps) => {
  const classes = cx([
    rootClassName,
    {
      [`${rootClassName}--negative`]: negative,
    },
    className,
  ]);

  return (
    <div className={classes} {...props}>
      {title && (
        <Heading tag={titleTag} variant={titleVariant} className={cx(`${rootClassName}__title`)}>
          {title}
        </Heading>
      )}
      <AccordionNegativeContext.Provider value={negative}>
        {children}
      </AccordionNegativeContext.Provider>
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: schema,
          }}
        />
      )}
    </div>
  );
};

Accordion.Item = AccordionItem;
Accordion.displayName = "Accordion";

export type { AccordionItemOnClickEvent, AccordionItemProps } from "./accordion-item";
